import { useEffect, useState } from "react"
import Popover from "@/v2-ui/popover"
import Separator from "@/v2-ui/separator"
import { errorToast, toast } from "@/v2-ui/toast"
import Button, { ConfirmButton } from "@/v2-backoffice-client/components/button"
import FeatureRenameDialog from "@/v2-backoffice-client/feature/rename/FeatureRenameDialog"
import { ENGLISH_LANGUAGE_CODE } from "@/v2-backoffice-client/translation/translation.constants"
import { useTranslationAiFillMutation } from "@/v2-backoffice-client/translation/catalog/translation.aiFill.mutation"
import { useTranslationHistoryQuery } from "@/v2-backoffice-client/translation/editor/translation.history.query"
import { useTranslationSuggestSetMutation } from "@/v2-backoffice-client/translation/editor/translation.suggestSet.mutation"
import { useTranslationUpsertMutation } from "@/v2-backoffice-client/translation/editor/translation.upsert.mutation"
import { useTranslationDeleteMutation } from "@/v2-backoffice-client/translation/orphaned/translation.delete.mutation"
import { useTranslationDeleteTypeValuesMutation } from "@/v2-backoffice-client/translation/deleteRows/translation.deleteTypeValues.mutation"
import TranslationEditorLanguage from "@/v2-backoffice-client/translation/editor/TranslationEditorLanguage"
import { FEATURE_USAGE } from "@/v2-backoffice-client/translation/featureUsage.generated"
import {
  blankEdit,
  isDirty,
  isSavable,
  type LangEdit,
  type SavedTranslationText,
} from "@/v2-backoffice-client/translation/editor/translation.editor.utils"
import type {
  SupportedLanguage,
  TranslationRow,
} from "@/v2-backoffice-client/translation/translation.types"

export type TranslationEditorProps = {
  row: TranslationRow
  languages: SupportedLanguage[]
  // Hosts embedding the editor outside the Translation tab (the layer
  // sheet's Details tab) hide the row delete: there the feature's lifecycle
  // is owned by the host object (deleting a layer's texts from under it
  // would break the layer), not by the translation catalog.
  canDelete?: boolean
  // Fired only when a save also wrote rows this editor cannot enumerate
  // (Apply to all linked features): the owner must refetch the catalog to
  // pick the sibling rows up.
  onSaved(): void
  // Fired with the exact texts a save / AI fill wrote for THIS row. The
  // owner patches the cached catalog with them (no refetch) — deferred
  // until the row collapses where an eager update would re-sort the table
  // (or drop the row from the Missing translations queue) mid-review.
  onSavedTexts?(texts: SavedTranslationText[]): void
  // Fired after a successful soft-delete of this exact row — the owner must
  // collapse/close it (the row no longer belongs in whatever list is open)
  // and refetch, since a delete can't be patched into the cache the way a
  // text edit can.
  onDeleted?(): void
  // Fired after a successful code rename. Same owner obligation as a
  // delete: this row's identity is gone from every open list, so collapse
  // and refetch rather than patch.
  onRenamed?(): void
}

function buildInitialEdits(
  row: TranslationRow,
  languages: SupportedLanguage[],
): Record<string, LangEdit> {
  const initial: Record<string, LangEdit> = {}
  for (const lang of languages) {
    const text = row.texts.find((t) => t.languageCode === lang.code)
    initial[lang.code] = blankEdit(text?.name ?? "", text?.description ?? "")
  }
  return initial
}

// Inline editor for one catalog row, rendered as the table's expanded row
// detail. Mount it with `key={row.code}` so the draft state resets when the
// operator opens a different row. AI suggestions are strictly on demand,
// always anchored on ONE operator-chosen source language ("Translate from"
// per language row) so the set has a single source of truth; unmount is the
// natural cancellation boundary for an in-flight suggest call.
//
// Layout note: the editor lives inside a full-table-width expanded row, and
// the catalog table can scroll horizontally. `.translation-editor` pins
// itself to the visible viewport (sticky + container query in
// translation.css), and actions are anchored LEFT so nothing ever renders
// off-screen.
function TranslationEditor(props: TranslationEditorProps) {
  const {
    row,
    languages,
    canDelete = true,
    onSaved,
    onSavedTexts,
    onDeleted,
    onRenamed,
  } = props

  const [isRenameOpen, setRenameOpen] = useState(false)

  const [edits, setEdits] = useState<Record<string, LangEdit>>(() =>
    buildInitialEdits(row, languages),
  )
  // Language code the last suggestion run was anchored on; doubles as the
  // "has generated" flag when non-null.
  const [sourceLang, setSourceLang] = useState<string | null>(null)
  const [aiError, setAiError] = useState(false)
  const [applyLinked, setApplyLinked] = useState(false)
  // Codes with an in-flight save: only THOSE rows show "Saving…". A single
  // shared loading flag made every language look busy when one was saved.
  const [savingCodes, setSavingCodes] = useState<ReadonlySet<string>>(
    () => new Set(),
  )
  const grouped = !!row.linkGroupCode

  const { data: historyData } = useTranslationHistoryQuery({
    variables: { code: row.code },
  })
  const history = historyData?.getFeatureHistory ?? []
  const lastChange = history[0]

  const [suggestSet, suggestState] = useTranslationSuggestSetMutation()
  const [upsert, upsertState] = useTranslationUpsertMutation()
  const [aiFill, aiFillState] = useTranslationAiFillMutation()
  const [removeFeature, removeFeatureState] = useTranslationDeleteMutation()
  const [removeTypeValue, removeTypeValueState] =
    useTranslationDeleteTypeValuesMutation()
  errorToast(upsertState.error, { title: "Save failed" })

  // The bulk AI fill (and other operators) write to the DB behind the
  // editor's back; when the refetched row arrives, sync the fields with the
  // fresh values — but never clobber an edit the operator has in progress.
  useEffect(() => {
    setEdits((prev) => {
      const fresh = buildInitialEdits(row, languages)
      for (const code of Object.keys(fresh)) {
        if (prev[code] && isDirty(prev[code])) fresh[code] = prev[code]
      }
      return fresh
    })
  }, [row, languages])

  function patch(code: string, next: Partial<LangEdit>) {
    setEdits((prev) =>
      prev[code] ? { ...prev, [code]: { ...prev[code], ...next } } : prev,
    )
  }

  // One coherent call for ALL languages anchored on the chosen source
  // language (they can't drift apart in meaning), then the critic verdicts
  // are folded into each language.
  async function suggestFrom(source: SupportedLanguage) {
    setAiError(false)
    setSourceLang(source.code)
    try {
      const res = await suggestSet({
        variables: {
          input: {
            Code: row.code,
            SourceLanguage: source.code,
            Texts: languages.map((lang) => {
              const edit = edits[lang.code]
              return {
                LanguageCode: lang.code,
                Name: edit?.curName ?? "",
                Description: edit?.curDesc || null,
              }
            }),
            TargetLanguages: languages.map((lang) => lang.code),
          },
        },
      })
      const set = res.data?.suggestAndEvaluate
      setEdits((prev) => {
        const next = { ...prev }
        for (const suggestion of set?.Languages ?? []) {
          const cur = next[suggestion.LanguageCode]
          if (!cur) continue
          next[suggestion.LanguageCode] = {
            ...cur,
            aiName: suggestion.Name,
            aiDesc: suggestion.Description ?? "",
            aiConfidence: suggestion.Confidence,
            aiNote: suggestion.Note ?? null,
            isImprovement: suggestion.IsImprovement,
            isConsistent: suggestion.IsConsistent,
            evalNote: suggestion.EvalNote ?? null,
          }
        }
        return next
      })
    } catch {
      setAiError(true)
      setSourceLang(null)
    }
  }

  async function saveLang(
    lang: SupportedLanguage,
  ): Promise<SavedTranslationText | null> {
    const edit = edits[lang.code]
    if (!edit || !isDirty(edit)) return null
    setSavingCodes((prev) => new Set(prev).add(lang.code))
    try {
      const res = await upsert({
        variables: {
          input: {
            Code: row.code,
            LanguageCode: lang.code,
            Name: edit.curName,
            Description: edit.curDesc || null,
            ApplyToLinked: applyLinked,
            // 'Feature' or 'TypeValue' — the row's kind decides which
            // common.Translations object the proc writes.
            ObjectType: row.objectType,
          },
        },
      })
      if (!res.data?.upsertTranslation) return null
      patch(lang.code, { baseName: edit.curName, baseDesc: edit.curDesc })
      return {
        LanguageCode: lang.code,
        Name: edit.curName,
        Description: edit.curDesc || null,
      }
    } finally {
      setSavingCodes((prev) => {
        const next = new Set(prev)
        next.delete(lang.code)
        return next
      })
    }
  }

  const dirtyLangs = languages.filter((lang) => {
    const edit = edits[lang.code]
    return edit ? isDirty(edit) : false
  })

  // Languages holding a savable suggestion that is not yet in the fields —
  // the "Apply all" set.
  const applicableLangs = languages.filter((lang) => {
    const edit = edits[lang.code]
    if (!edit || sourceLang === null || !isSavable(edit)) return false
    return edit.aiName !== edit.curName || edit.aiDesc !== edit.curDesc
  })

  function onApplyAll() {
    setEdits((prev) => {
      const next = { ...prev }
      for (const lang of applicableLangs) {
        const edit = next[lang.code]
        next[lang.code] = {
          ...edit,
          curName: edit.aiName,
          curDesc: edit.aiDesc,
        }
      }
      return next
    })
  }

  // A save that used "Apply to all linked features" also wrote sibling rows
  // whose values this editor cannot enumerate — only that case needs the
  // owner's full refetch; everything else is patched into the cache from
  // the exact texts saved.
  function reportSaved(saved: SavedTranslationText[]) {
    if (saved.length === 0) return
    if (grouped && applyLinked) onSaved()
    else onSavedTexts?.(saved)
  }

  async function onSaveLang(lang: SupportedLanguage) {
    const saved = await saveLang(lang)
    if (saved) reportSaved([saved])
  }

  async function onSaveAll() {
    const results = await Promise.all(dirtyLangs.map((lang) => saveLang(lang)))
    reportSaved(results.filter((saved) => saved !== null))
  }

  // Languages with no text in the fields — what the one-row AI fill targets.
  const missingLangs = languages.filter(
    (lang) => (edits[lang.code]?.curName ?? "").trim() === "",
  )

  // Same contract as the multi-select "AI translate missing" action, for
  // just this row: drafts and SAVES every missing language directly, never
  // touching languages that already have text. The saved drafts are applied
  // straight into the fields below (the mutation echoes them back), so the
  // operator reviews them right here — no refetch, no table re-sort, and the
  // row stays put until it is collapsed (onSavedTexts is applied deferred).
  async function onAiFillMissing() {
    try {
      const res = await aiFill({
        variables: {
          input: {
            Items: [
              {
                Code: row.code,
                ObjectType: row.objectType,
                Texts: languages
                  .filter(
                    (lang) => (edits[lang.code]?.curName ?? "").trim() !== "",
                  )
                  .map((lang) => ({
                    LanguageCode: lang.code,
                    Name: edits[lang.code].curName,
                    Description: edits[lang.code].curDesc || null,
                  })),
              },
            ],
          },
        },
      })
      const result = res.data?.aiFillMissingTranslations?.[0]
      if (result?.Ok) {
        // Show the saved drafts in the fields, as clean (non-dirty) values.
        // A language the operator started typing in mid-flight was not part
        // of the fill (it wasn't missing at click time) and is left alone.
        setEdits((prev) => {
          const next = { ...prev }
          for (const filled of result.Filled) {
            const edit = next[filled.LanguageCode]
            if (!edit || edit.curName.trim() !== "") continue
            next[filled.LanguageCode] = {
              ...edit,
              baseName: filled.Name,
              curName: filled.Name,
              baseDesc: filled.Description ?? "",
              curDesc: filled.Description ?? "",
            }
          }
          return next
        })
        toast({
          title:
            result.FilledCount === 0
              ? "Nothing to translate — every language already has text."
              : `Drafted and saved ${result.FilledCount} language(s) below.`,
          description:
            result.FilledCount === 0
              ? undefined
              : "The texts are live in the translation database, flagged as AI-written until a human edits them. Adjust anything that reads wrong and hit Save — or leave them as they are.",
        })
        // The DB wrote every Filled entry, including languages the operator
        // started typing in mid-flight (skipped in the fields above): the
        // cache patch must mirror the DB, not the fields.
        onSavedTexts?.(
          result.Filled.map((filled) => ({
            LanguageCode: filled.LanguageCode,
            Name: filled.Name,
            Description: filled.Description ?? null,
          })),
        )
      } else {
        toast({
          variant: "destructive",
          title: "AI translate failed",
          description: result?.Error ?? "unknown error",
        })
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "AI translate failed",
        description: error instanceof Error ? error.message : String(error),
      })
    }
  }

  const deleting = removeFeatureState.loading || removeTypeValueState.loading

  // Same soft-delete mutations as the table's bulk delete action
  // (TranslationDeleteRowsAction), aimed at just this one row.
  async function onDelete() {
    try {
      let result:
        | { Code: string; Ok: boolean; Error?: string | null }
        | undefined
      if (row.objectType === "Feature") {
        const res = await removeFeature({
          variables: { codes: [row.code], isDeleted: true },
        })
        if (res.errors?.length) throw new Error(res.errors[0].message)
        result = res.data?.setFeaturesDeleted?.[0]
      } else {
        const res = await removeTypeValue({
          variables: { objectKeys: [row.code], isDeleted: true },
        })
        if (res.errors?.length) throw new Error(res.errors[0].message)
        result = res.data?.setTypeValuesDeleted?.[0]
      }
      if (!result?.Ok) {
        throw new Error(result?.Error ?? "The row could not be deleted.")
      }
      toast({ title: `Deleted ${row.code} (soft delete).` })
      onDeleted?.()
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Delete failed",
        description: error instanceof Error ? error.message : String(error),
      })
    }
  }

  const usage = FEATURE_USAGE[row.code] ?? []

  return (
    <div className="translation-editor">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
        <span className="flex items-center gap-2 text-sm font-medium">
          <span className="break-all">{row.code}</span>
          {grouped ? (
            <span
              className="rounded bg-primary/15 px-1.5 py-0.5 text-[11px] font-normal text-primary"
              title={`Linked group: ${row.linkGroupCode}`}
            >
              {row.isLinkMaster ? "linked · master" : "linked"}
            </span>
          ) : null}
        </span>
        {lastChange ? (
          <Popover>
            <Popover.Trigger asChild>
              <button
                type="button"
                data-feature-code="UI_BackofficeTranslation_Editor_History"
                className="text-xs text-muted-foreground underline decoration-dotted underline-offset-2 hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                title="Show the full change history for this feature"
              >
                Last change: {lastChange.ChangedAt}, {lastChange.User}
              </button>
            </Popover.Trigger>
            <Popover.Content
              align="start"
              className="translation-editor-history"
            >
              <p className="translation-editor-history-title">
                Change history (newest first)
              </p>
              <ul className="translation-editor-history-list">
                {history.map((entry, i) => (
                  <li key={i} className="translation-editor-history-entry">
                    <span className="tabular-nums text-muted-foreground">
                      {entry.ChangedAt}
                    </span>
                    <span>{entry.User}</span>
                    <span className="text-muted-foreground">
                      {entry.Scope ??
                        (entry.Area === "Feature" ? "feature" : "")}
                    </span>
                  </li>
                ))}
              </ul>
            </Popover.Content>
          </Popover>
        ) : null}
        {usage.length ? (
          <span className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
            <span>Used on:</span>
            {usage.map((link) => (
              <a
                key={`${link.url}:${link.label}`}
                href={link.url}
                target="_blank"
                rel="noreferrer"
                // The component files behind this surface, so an operator can
                // locate the exact spot the text renders (the URL alone is
                // often just "/map").
                title={link.files.join("\n")}
                data-feature-code="UI_BackofficeTranslation_UsedOnLink"
                className="text-primary hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {link.label} ↗
              </a>
            ))}
          </span>
        ) : null}
      </div>

      {aiError ? (
        <p className="text-xs text-destructive">
          AI suggestion failed — try Translate from again.
        </p>
      ) : null}

      <div className="translation-editor-languages">
        {languages.map((lang) => {
          const edit = edits[lang.code]
          if (!edit) return null
          return (
            <TranslationEditorLanguage
              key={lang.code}
              lang={lang}
              edit={edit}
              isSource={sourceLang === lang.code}
              hasGenerated={sourceLang !== null && !suggestState.loading}
              aiLoading={suggestState.loading}
              isSaving={savingCodes.has(lang.code)}
              disabled={deleting}
              onPatch={(next) => patch(lang.code, next)}
              onApplySuggestion={() =>
                patch(lang.code, { curName: edit.aiName, curDesc: edit.aiDesc })
              }
              onSave={() => onSaveLang(lang)}
              onSuggestFrom={() => suggestFrom(lang)}
            />
          )
        })}
      </div>

      <div className="translation-editor-footer">
        <Button
          size="sm"
          data-feature-code="UI_BackofficeTranslation_SaveAll"
          onClick={onSaveAll}
          title="Write every changed language's current text to common.Translations in one pass (respects 'Apply to all linked features' when ticked)."
          disabled={dirtyLangs.length === 0 || upsertState.loading || deleting}
          isLoading={upsertState.loading}
        >
          {upsertState.loading
            ? "Saving…"
            : dirtyLangs.length > 0
              ? `Save all changes (${dirtyLangs.length})`
              : "Save all changes"}
        </Button>
        {applicableLangs.length > 0 ? (
          <Button
            size="sm"
            variant="secondary"
            data-feature-code="UI_BackofficeTranslation_ApplyAll"
            onClick={onApplyAll}
            title="Copy every pending AI suggestion into its language's Name/Description fields (nothing is written until Save)."
          >
            Apply all suggestions ({applicableLangs.length})
          </Button>
        ) : null}
        {missingLangs.length > 0 ? (
          <Button
            size="sm"
            variant="secondary"
            iconSrc="magic-wand"
            data-feature-code="UI_BackofficeTranslation_Editor_AiFill"
            onClick={onAiFillMissing}
            disabled={aiFillState.loading || deleting}
            isLoading={aiFillState.loading}
            title="AI-draft AND save this row's missing languages in one call, then show them in the fields below for review (languages that already have text are never touched). A fully empty row is drafted from its code — e.g. a NACE classification code gets its official name."
          >
            {aiFillState.loading
              ? "Translating…"
              : `AI translate missing (${missingLangs.length})`}
          </Button>
        ) : null}
        {grouped ? (
          <label
            className="flex items-center gap-2 text-xs text-muted-foreground"
            title={`Also write the same text to every other feature in group ${row.linkGroupCode}`}
          >
            <input
              type="checkbox"
              checked={applyLinked}
              onChange={(e) => setApplyLinked(e.target.checked)}
              data-feature-code="UI_BackofficeTranslation_ApplyToLinked"
            />
            Apply to all linked features
          </label>
        ) : null}

        {canDelete ? (
          <>
            <Separator orientation="vertical" />

            {row.objectType === "Feature" ? (
              <Button
                size="sm"
                variant="outline"
                iconSrc="pencil"
                disabled={
                  deleting ||
                  upsertState.loading ||
                  suggestState.loading ||
                  aiFillState.loading
                }
                title="Rename the feature code, carrying every reference (text, availability, favourites, saved values) with it in both the runtime and the authoring catalog."
                data-feature-code="UI_BackofficeTranslation_Editor_RenameCode"
                onClick={() => setRenameOpen(true)}
              >
                Rename code…
              </Button>
            ) : null}

            <ConfirmButton
              size="sm"
              variant="destructive"
              iconSrc="trash"
              disabled={
                deleting ||
                upsertState.loading ||
                suggestState.loading ||
                aiFillState.loading
              }
              isLoading={deleting}
              content="Soft-delete this row from the translation database. History is kept and it can be restored, but the text stops being published."
              confirmContent={`Delete ${row.code} from the translation database? This is a soft delete — history is kept.`}
              confirmVariant="destructive"
              onConfirm={onDelete}
              data-feature-code="UI_BackofficeTranslation_Editor_Delete"
            >
              Delete
            </ConfirmButton>
          </>
        ) : null}
      </div>

      {row.objectType === "Feature" ? (
        <FeatureRenameDialog
          featureCode={row.code}
          name={
            row.texts.find(
              (text) => text.languageCode === ENGLISH_LANGUAGE_CODE,
            )?.name
          }
          open={isRenameOpen}
          onOpenChange={setRenameOpen}
          onRenamed={() => onRenamed?.()}
        />
      ) : null}
    </div>
  )
}

export default TranslationEditor
